import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { handleUnexpectedError } from '@/app/errors';
import { updatePaperlessBillingSetting } from '@/app/network/firefly-authenticated-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Heading } from '@/common/components/heading';
import { EmptyState, EmptyStateImage, EmptyStateMessage } from '@/shared/components/empty-states';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

export const Route = createFileRoute('/memberships/setup/$member-number/go-paperless')({
  component: GoPaperlessPage
});

function GoPaperlessPage() {
  const { membership } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync } = useMutation({
    mutationFn: async (value: boolean) => {
      await updatePaperlessBillingSetting(membership.member_number, value);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      await navigate({ to: '/memberships/setup/$member-number/automatic-payments', params: { 'member-number': membership.member_number.toString() } });
    },
    onError: error => handleUnexpectedError(error)
  });

  const handlePaperless = async (paperless: boolean) => {
    await mutateAsync(paperless);
  };

  return (
    <>
      <Heading>Go Paperless</Heading>

      <EmptyState>
        <EmptyStateImage src="/img/wizard_paperless.svg" alt="An illustration of a laptop with three green leaves on the screen."/>
        <EmptyStateMessage>
          Would you like to sign up for paperless billing?
        </EmptyStateMessage>
        <ButtonGroup vertical className="min-w-52">
          <Button onClick={() => handlePaperless(true)}>Go Paperless</Button>
          <Button onClick={() => handlePaperless(false)} outline>No Thanks</Button>
        </ButtonGroup>
      </EmptyState>
    </>
  );
}
